import React, { useRef, useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import InputField from "../InputField/InputField";
import type { InputFieldPropsBase } from "../InputField/InputField";
import { format, parse, isValid } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { z } from "zod";

type DatetimepickerMode = "date" | "time" | "datetime";
type DatetimepickerLocale = "en" | "pt";

export interface DatetimepickerI18nMessages {
  invalidDate?: string;
  invalidTime?: string;
  invalidDateTime?: string;
  incomplete?: string;
  outOfRange?: string;
  placeholderDate?: string;
  placeholderTime?: string;
  placeholderDateTime?: string;
}

export interface DatetimepickerProps
  extends Omit<InputFieldPropsBase, "type" | "value" | "onChange"> {
  value: Date | string | null;
  onChange: (date: Date | string | null) => void;
  mode?: DatetimepickerMode;
  minDate?: Date | string;
  maxDate?: Date | string;
  locale?: DatetimepickerLocale;
  datetimepickerI18nMessages?: DatetimepickerI18nMessages;
  emitISO?: boolean;
  customFormats?: Partial<typeof formats>;
  customParseFormats?: Partial<typeof parseFormats>;
  customMasks?: Partial<typeof masks>;
  placeholder?: string;
}

const localeMap = {
  en: enUS,
  pt: ptBR,
};

const masks = {
  date: "99/99/9999",
  time: "99:99[:99]", // segundos opcionais
  datetime: "99/99/9999 99:99[:99]",
};

const formats = {
  date: "P",
  time: "HH:mm:ss", // agora sempre mostra segundos
  datetime: "P HH:mm:ss",
};

const parseFormats = {
  date: "dd/MM/yyyy",
  time: "HH:mm:ss",
  datetime: "dd/MM/yyyy HH:mm:ss",
};

const defaultI18n: Record<DatetimepickerLocale, DatetimepickerI18nMessages> = {
  en: {
    invalidDate: "Invalid date format",
    invalidTime: "Invalid time format",
    invalidDateTime: "Invalid date/time format",
    incomplete: "Incomplete date/time",
    outOfRange: "Date/time out of allowed range",
    placeholderDate: "MM/DD/YYYY",
    placeholderTime: "HH:mm",
    placeholderDateTime: "MM/DD/YYYY HH:mm",
  },
  pt: {
    invalidDate: "Formato de data inválido",
    invalidTime: "Formato de hora inválido",
    invalidDateTime: "Formato de data/hora inválido",
    incomplete: "Data/hora incompleta",
    outOfRange: "Data/hora fora do intervalo permitido",
    placeholderDate: "DD/MM/AAAA",
    placeholderTime: "HH:mm",
    placeholderDateTime: "DD/MM/AAAA HH:mm",
  },
};

function parseToDate(value: string | Date | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  // Try ISO string
  const isoMatch = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?Z?$/.test(value);
  if (isoMatch) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  // Try dd/MM/yyyy or dd/MM/yyyy HH:mm[:ss]
  if (/^\d{2}\/\d{2}\/\d{4}( \d{2}:\d{2}(:\d{2})?)?$/.test(value)) {
    let d = parse(value, "dd/MM/yyyy HH:mm:ss", new Date());
    if (!isValid(d)) d = parse(value, "dd/MM/yyyy HH:mm", new Date());
    if (!isValid(d)) d = parse(value, "dd/MM/yyyy", new Date());
    return isValid(d) ? d : null;
  }
  // Try HH:mm[:ss] (time only)
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    let d = parse(value, "HH:mm:ss", new Date());
    if (!isValid(d)) d = parse(value, "HH:mm", new Date());
    return isValid(d) ? d : null;
  }
  return null;
}

// COMPONENT

const Datetimepicker: React.FC<DatetimepickerProps> = ({
  value,
  onChange,
  mode = "datetime",
  placeholder,
  disabled,
  minDate,
  maxDate,
  locale = "pt",
  label,
  required,
  helperText,
  error,
  errorMessage,
  successMessage,
  successIcon,
  className,
  inputClassName,
  inputProps,
  containerProps,
  tooltip,
  tabIndex,
  autoFocus,
  id,
  accessibilityLabel,
  accessibilityErrorMessage,
  onFocus,
  onBlur,
  datetimepickerI18nMessages,
  emitISO,
  customFormats,
  customParseFormats,
  customMasks,
  ...rest
}) => {
  // Merge i18n
  const i18n = React.useMemo(
    () => ({ ...defaultI18n[locale], ...datetimepickerI18nMessages }),
    [locale, datetimepickerI18nMessages]
  );

  // Allow custom formats/masks
  const usedFormats = React.useMemo(() => ({ ...formats, ...customFormats }), [customFormats]);
  const usedParseFormats = React.useMemo(() => ({ ...parseFormats, ...customParseFormats }), [customParseFormats]);
  const usedMasks = React.useMemo(() => ({ ...masks, ...customMasks }), [customMasks]);

  const [inputValue, setInputValue] = useState<string>("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const d = parseToDate(value);
    if (d && isValid(d)) {
      setInputValue(format(d, usedFormats[mode], { locale: localeMap[locale] }));
    } else {
      setInputValue("");
    }
  }, [value, mode, locale, usedFormats]);

  const isDateInRange = useCallback((d: Date | null) => {
    if (!d || !isValid(d)) return false;
    const min = parseToDate(minDate ?? null);
    const max = parseToDate(maxDate ?? null);
    if (min && d < min) return false;
    if (max && d > max) return false;
    return true;
  }, [minDate, maxDate]);

  const getZodSchema = useCallback(() => {
    let schema = z.string().min(1, {
      message: i18n.incomplete,
    });

    if (mode === "date") {
      schema = schema.regex(/^\d{2}\/\d{2}\/\d{4}$/, {
        message: i18n.invalidDate,
      });
    } else if (mode === "time") {
      schema = schema.regex(/^\d{2}:\d{2}(:\d{2})?$/, {
        message: i18n.invalidTime,
      });
    } else {
      schema = schema.regex(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}(:\d{2})?$/, {
        message: i18n.invalidDateTime,
      });
    }

    return schema;
  }, [i18n, mode]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | string) => {
      const val = typeof e === "string" ? e : e.target.value;
      setInputValue(val);
      setInternalError(null);

      // Aceita ISO direto
      let parsed: Date | null = null;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?Z?$/.test(val)) {
        const d = new Date(val);
        if (isValid(d)) parsed = d;
      }
      if (!parsed) {
        const schema = getZodSchema();
        const result = schema.safeParse(val);
        if (!result.success) {
          setInternalError(result.error.errors[0].message);
          onChange(null);
          return;
        }
        parsed = parse(val, usedParseFormats[mode], new Date(), { locale: localeMap[locale] });
        if (!isValid(parsed)) {
          setInternalError(i18n.invalidDateTime ?? "Invalid date/time");
          onChange(null);
          return;
        }
      }
      if (!isDateInRange(parsed)) {
        setInternalError(i18n.outOfRange ?? "Date/time out of allowed range");
        onChange(null);
        return;
      }
      setInternalError(null);
      if (emitISO) {
        onChange(parsed.toISOString());
      } else {
        onChange(parsed);
      }
    },
    [emitISO, i18n, locale, mode, onChange, getZodSchema, isDateInRange, usedParseFormats]
  );

  const handleFocusMemo = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (typeof onFocus === "function") onFocus(e);
    },
    [onFocus]
  );

  const handleBlurMemo = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (typeof onBlur === "function") onBlur(e);
    },
    [onBlur]
  );

  const handleCalendarSelect = useCallback((date: Date) => {
    setShowCalendar(false);
    let finalDate = date;
    if (mode === "datetime" && value) {
      const valueDate = parseToDate(value);
      if (valueDate && isValid(valueDate)) {
        finalDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          valueDate.getHours(),
          valueDate.getMinutes(),
          valueDate.getSeconds()
        );
      }
    }
    onChange(emitISO ? finalDate.toISOString() : finalDate);
  }, [emitISO, mode, onChange, value]);

  const resolvedPlaceholder =
    placeholder ||
    (mode === "date"
      ? i18n.placeholderDate
      : mode === "time"
      ? i18n.placeholderTime
      : i18n.placeholderDateTime);

  function isInputOutOfRange(val: string, minDate?: Date | string, maxDate?: Date | string): boolean {
    const d = parseToDate(val);
    if (!d || !isValid(d)) return false;
    const min = minDate ? parseToDate(minDate) : undefined;
    const max = maxDate ? parseToDate(maxDate) : undefined;
    if (min && d < min) return true;
    if (max && d > max) return true;
    return false;
  }

  const handleInput = useCallback(
    (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const val = (e.target as HTMLInputElement | HTMLTextAreaElement).value;
      if (isInputOutOfRange(val, minDate, maxDate)) {
        e.preventDefault();
        setInternalError(i18n.outOfRange ?? "Date/time out of allowed range");
      }
    },
    [minDate, maxDate, i18n]
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    setInternalError(null);
    onChange(null);
  }, [onChange]);

  function isMobileDevice() {
    if (typeof window === "undefined") return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  const handleInputFieldChange = useCallback((val: unknown) => {
    if (typeof val === "string") handleChange(val);
  }, [handleChange]);

  if (isMobileDevice()) {
    if (mode === "date") {
      return (
        <input
          type="date"
          value={value && parseToDate(value) ? format(parseToDate(value)!, "yyyy-MM-dd") : ""}
          onChange={e => {
            const val = e.target.value;
            if (!val) return onChange(null);
            const date = new Date(val);
            onChange(emitISO ? date.toISOString() : date);
          }}
          min={minDate ? format(parseToDate(minDate)!, "yyyy-MM-dd") : undefined}
          max={maxDate ? format(parseToDate(maxDate)!, "yyyy-MM-dd") : undefined}
          disabled={disabled}
          required={required}
          className={clsx("custom-datetimepicker-mobile", className)}
          id={id}
          aria-label={accessibilityLabel}
          aria-errormessage={accessibilityErrorMessage}
          tabIndex={tabIndex}
          autoFocus={autoFocus}
          data-testid="datetimepicker-mobile-date"
        />
      );
    }
    if (mode === "time") {
      return (
        <input
          type="time"
          step="1"
          value={value && parseToDate(value) ? format(parseToDate(value)!, "HH:mm:ss") : ""}
          onChange={e => {
            const val = e.target.value;
            if (!val) return onChange(null);
            const today = new Date();
            const [h, m, s] = val.split(":").map(Number);
            today.setHours(h, m, s || 0, 0);
            onChange(emitISO ? today.toISOString() : today);
          }}
          disabled={disabled}
          required={required}
          className={clsx("custom-datetimepicker-mobile", className)}
          id={id}
          aria-label={accessibilityLabel}
          aria-errormessage={accessibilityErrorMessage}
          tabIndex={tabIndex}
          autoFocus={autoFocus}
          data-testid="datetimepicker-mobile-time"
        />
      );
    }
    if (mode === "datetime") {
      const dateValue = value && parseToDate(value) ? format(parseToDate(value)!, "yyyy-MM-dd") : "";
      const timeValue = value && parseToDate(value) ? format(parseToDate(value)!, "HH:mm:ss") : "";

      return (
        <div className={clsx("custom-datetimepicker-mobile", className)} data-testid="datetimepicker-mobile-datetime">
          <input
            type="date"
            value={dateValue}
            onChange={e => {
              const val = e.target.value;
              if (!val) return onChange(null);
              const date = new Date(val);
              if (value && parseToDate(value)) {
                const old = parseToDate(value)!;
                date.setHours(old.getHours(), old.getMinutes(), old.getSeconds(), 0);
              }
              onChange(emitISO ? date.toISOString() : date);
            }}
            min={minDate ? format(parseToDate(minDate)!, "yyyy-MM-dd") : undefined}
            max={maxDate ? format(parseToDate(maxDate)!, "yyyy-MM-dd") : undefined}
            disabled={disabled}
            required={required}
            id={id ? `${id}-date` : undefined}
            aria-label={accessibilityLabel ? `${accessibilityLabel} (data)` : undefined}
            aria-errormessage={accessibilityErrorMessage}
            tabIndex={tabIndex}
            autoFocus={autoFocus}
            style={{ marginBottom: 8 }}
            data-testid="datetimepicker-mobile-date"
          />
          <input
            type="time"
            step="1"
            value={timeValue}
            onChange={e => {
              const val = e.target.value;
              if (!val) return onChange(null);
              const date = value && parseToDate(value) ? new Date(parseToDate(value)!) : new Date();
              const [h, m, s] = val.split(":").map(Number);
              date.setHours(h, m, s || 0, 0);
              onChange(emitISO ? date.toISOString() : date);
            }}
            disabled={disabled}
            required={required}
            id={id ? `${id}-time` : undefined}
            aria-label={accessibilityLabel ? `${accessibilityLabel} (hora)` : undefined}
            aria-errormessage={accessibilityErrorMessage}
            tabIndex={tabIndex}
            data-testid="datetimepicker-mobile-time"
          />
        </div>
      );
    }
  }

  return (
    <div
      className={clsx("relative", "custom-datetimepicker", containerProps?.className)}
      ref={anchorRef}
      data-testid="datetimepicker-container"
      {...containerProps}
    >
      <InputField
        {...rest}
        id={id}
        type="text" // <-- Sempre text, sem validação HTML extra
        value={inputValue ?? ""}
        onChange={handleInputFieldChange}
        label={label}
        required={required}
        helperText={helperText}
        error={error || !!internalError}
        errorMessage={internalError || errorMessage}
        successMessage={successMessage}
        successIcon={successIcon}
        className={className}
        inputClassName={inputClassName}
        placeholder={resolvedPlaceholder}
        inputProps={{
          ...inputProps,
          disabled,
          id,
          "aria-label": accessibilityLabel,
          "aria-errormessage": accessibilityErrorMessage,
          onFocus: handleFocusMemo,
          onBlur: handleBlurMemo,
          "aria-live": "polite",
          onInput: handleInput,
        }}
        data-testid="datetimepicker-input"
        containerProps={containerProps}
        tooltip={tooltip}
        tabIndex={tabIndex}
        autoFocus={autoFocus}
        mask={usedMasks[mode]} // <-- Só máscara visual
        // NÃO passe validationRegex, customValidation, etc!
        // NÃO passe type="date" ou "datetime"!
        rightIcon={
          <span className="flex items-center space-x-1">
            {inputValue && !disabled && (
              <button
                type="button"
                aria-label="Limpar campo"
                className="cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none"
                style={{ background: "none", border: "none", padding: 0, marginRight: 4 }}
                onClick={handleClear}
                tabIndex={0}
                data-testid="datetimepicker-clear-button"
              >
                <ClearIcon />
              </button>
            )}
            <span
              className="cursor-pointer flex items-center"
              tabIndex={0}
              aria-label="Abrir calendário"
              data-testid="datetimepicker-calendar-icon"
              onClick={() => setShowCalendar((v) => !v)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setShowCalendar((v) => !v);
              }}
            >
              <CalendarIcon />
            </span>
          </span>
        }
      />
      {showCalendar && mode !== "time" && (
        <FluentCalendar
          selected={parseToDate(value)}
          onSelect={handleCalendarSelect}
          minDate={(() => { const d = minDate ? parseToDate(minDate) : undefined; return d === null ? undefined : d; })()}
          maxDate={(() => { const d = maxDate ? parseToDate(maxDate) : undefined; return d === null ? undefined : d; })()}
          locale={locale}
          onClose={() => setShowCalendar(false)}
          anchorRef={anchorRef}
          dataTestId="datetimepicker-calendar-popup"
        />
      )}
      {showCalendar && mode === "time" && (
        <FluentTimePicker
          selected={parseToDate(value)}
          onSelect={date => {
            onChange(emitISO ? date.toISOString() : date);
            setShowCalendar(false);
          }}
          onClose={() => setShowCalendar(false)}
          dataTestId="datetimepicker-timepicker-popup"
        />
      )}
    </div>
  );
};

// Calendar Icon
const CalendarIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 20 20" aria-hidden="true">
    <rect x="3" y="6" width="14" height="11" rx="2" fill="#888" />
    <rect x="3" y="3" width="14" height="3" rx="1" fill="#bbb" />
    <rect x="6" y="1" width="2" height="4" rx="1" fill="#888" />
    <rect x="12" y="1" width="2" height="4" rx="1" fill="#888" />
  </svg>
);

// Calendar
const FluentCalendar: React.FC<{
  selected: Date | null;
  onSelect: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  locale?: DatetimepickerLocale;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  dataTestId?: string;
}> = ({ selected, onSelect, minDate, maxDate, locale, onClose, anchorRef, dataTestId }) => {
  const [month, setMonth] = useState(() => selected ?? new Date());

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorRef]);

  // Dias da semana
  const weekDays = locale === "pt"
    ? ["S", "T", "Q", "Q", "S", "S", "D"]
    : ["S", "M", "T", "W", "T", "F", "S"];
  const startOfWeek = locale === "pt" ? 1 : 0;

  // Calcula o primeiro dia do mês (ajustando para início da semana)
  const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const firstDay = (firstDayOfMonth.getDay() - startOfWeek + 7) % 7;

  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const today = new Date();

  function handleDayClick(day: number) {
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    onSelect(date);
    onClose();
  }

  return (
    <div
      className={clsx(
        "absolute z-[1000]",
        // Glassmorphism Fluent UI
        "bg-white/60 dark:bg-[#201f1e]/60",
        "border border-gray-200 dark:border-[#292827]",
        "rounded-xl shadow-[0_4px_24px_0_rgba(0,120,212,0.10)] dark:shadow-[0_4px_24px_0_rgba(255,255,255,0.10)]",
        "backdrop-blur-md backdrop-saturate-150",
        "transition-colors transition-shadow transition-[background] duration-200",
        "p-4 mt-1 min-w-[240px]",
        "left-0 top-full",
        "fluent-calendar-popup"
      )}
      role="dialog"
      aria-modal="true"
      data-testid={dataTestId}
      tabIndex={-1}
      onKeyDown={e => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="flex justify-between items-center mb-2">
        <button
          aria-label="Mês anterior"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          className="bg-none border-none cursor-pointer text-lg px-2 py-1 font-grotesk text-blue-700 dark:text-blue-300"
          type="button"
        >{"‹"}</button>
        <span className="font-inter font-semibold text-base text-gray-900 dark:text-white">
          {month.toLocaleString(locale === "en" ? "en-US" : "pt-BR", {
            month: "long",
            year: "numeric",
          })}
        </span>
        <button
          aria-label="Próximo mês"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          className="bg-none border-none cursor-pointer text-lg px-2 py-1 font-grotesk text-blue-700 dark:text-blue-300"
          type="button"
        >{"›"}</button>
      </div>
      <table className="w-full mt-2 border-collapse font-grotesk">
        <thead>
          <tr>
            {weekDays.map((d, i) => (
              <th key={i} className="font-normal text-gray-400 dark:text-gray-500 p-1 font-grotesk">{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: Math.ceil((daysInMonth + firstDay) / 7) }).map((_, weekIdx) => (
            <tr key={weekIdx}>
              {Array.from({ length: 7 }).map((_, dayIdx) => {
                const day = weekIdx * 7 + dayIdx - firstDay + 1;
                const date = new Date(month.getFullYear(), month.getMonth(), day);
                const isDisabled =
                  day < 1 ||
                  day > daysInMonth ||
                  (minDate && date < minDate) ||
                  (maxDate && date > maxDate);
                const isSelected =
                  selected &&
                  date.getFullYear() === selected.getFullYear() &&
                  date.getMonth() === selected.getMonth() &&
                  date.getDate() === selected.getDate();
                const isToday =
                  date.getFullYear() === today.getFullYear() &&
                  date.getMonth() === today.getMonth() &&
                  date.getDate() === today.getDate();
                return (
                  <td key={dayIdx} className="p-0">
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => !isDisabled && handleDayClick(day)}
                      className={clsx(
                        "w-8 h-8 m-0.5 rounded-md outline-none transition-colors font-grotesk",
                        isSelected
                          ? "border-2 border-blue-600 bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-200 font-semibold"
                          : isToday
                          ? "bg-blue-50 dark:bg-blue-800 text-blue-700 dark:text-blue-200"
                          : "",
                        isDisabled
                          ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                          : "text-gray-900 dark:text-white cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-800"
                      )}
                      tabIndex={isDisabled ? -1 : 0}
                    >
                      {day > 0 && day <= daysInMonth ? day : ""}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <button
        className="mt-2 bg-none border-none text-blue-600 dark:text-blue-300 cursor-pointer text-sm font-grotesk"
        onClick={onClose}
        type="button"
        data-testid="datetimepicker-calendar-close"
      >
        Fechar
      </button>
    </div>
  );
};

// --- Time Picker ---

const FluentTimePicker: React.FC<{
  selected: Date | null;
  onSelect: (date: Date) => void;
  onClose: () => void;
  dataTestId?: string;
}> = ({ selected, onSelect, onClose, dataTestId }) => {
  const [hour, setHour] = useState(selected ? selected.getHours() : 0);
  const [minute, setMinute] = useState(selected ? selected.getMinutes() : 0);
  const [second, setSecond] = useState(selected ? selected.getSeconds() : 0);

  useEffect(() => {
    if (selected) {
      setHour(selected.getHours());
      setMinute(selected.getMinutes());
      setSecond(selected.getSeconds());
    }
  }, [selected]);

  function handleSelect() {
    const date = selected ? new Date(selected) : new Date();
    date.setHours(hour, minute, second, 0);
    onSelect(date);
    onClose();
  }

  return (
    <div
      className={clsx(
        "absolute z-[1000]",
        // Glassmorphism Fluent UI
        "bg-white/60 dark:bg-[#201f1e]/60",
        "border border-gray-200 dark:border-[#292827]",
        "rounded-xl shadow-[0_4px_24px_0_rgba(0,120,212,0.10)] dark:shadow-[0_4px_24px_0_rgba(255,255,255,0.10)]",
        "backdrop-blur-md backdrop-saturate-150",
        "transition-colors transition-shadow transition-[background] duration-200",
        "p-4 mt-1 min-w-[220px]",
        "left-0 top-full",
        "fluent-timepicker-popup"
      )}
      role="dialog"
      aria-modal="true"
      data-testid={dataTestId}
      tabIndex={-1}
      onKeyDown={e => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="flex items-center justify-center space-x-2 font-grotesk">
        <select
          value={hour}
          onChange={e => setHour(Number(e.target.value))}
          className="border rounded px-2 py-1 font-grotesk bg-white/80 dark:bg-[#232322]/80 text-gray-900 dark:text-white"
          aria-label="Hora"
        >
          {Array.from({ length: 24 }).map((_, h) => (
            <option key={h} value={h}>{h.toString().padStart(2, "0")}</option>
          ))}
        </select>
        <span>:</span>
        <select
          value={minute}
          onChange={e => setMinute(Number(e.target.value))}
          className="border rounded px-2 py-1 font-grotesk bg-white/80 dark:bg-[#232322]/80 text-gray-900 dark:text-white"
          aria-label="Minuto"
        >
          {Array.from({ length: 60 }).map((_, m) => (
            <option key={m} value={m}>{m.toString().padStart(2, "0")}</option>
          ))}
        </select>
        <span>:</span>
        <select
          value={second}
          onChange={e => setSecond(Number(e.target.value))}
          className="border rounded px-2 py-1 font-grotesk bg-white/80 dark:bg-[#232322]/80 text-gray-900 dark:text-white"
          aria-label="Segundo"
        >
          {Array.from({ length: 60 }).map((_, s) => (
            <option key={s} value={s}>{s.toString().padStart(2, "0")}</option>
          ))}
        </select>
      </div>
      <div className="flex justify-between mt-3">
        <button
          className="text-blue-600 dark:text-blue-300 text-sm font-grotesk"
          onClick={handleSelect}
          type="button"
          data-testid="datetimepicker-timepicker-ok"
          aria-label="Confirmar horário"
        >
          OK
        </button>
        <button
          className="text-gray-500 dark:text-gray-400 text-sm font-grotesk"
          onClick={onClose}
          type="button"
          data-testid="datetimepicker-timepicker-close"
          aria-label="Fechar seletor de horário"
        >
          Fechar
        </button>
      </div>
    </div>
  );
};

const ClearIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="7" stroke="#888" strokeWidth="1.5" fill="#fff"/>
    <path d="M5.5 5.5l5 5m0-5l-5 5" stroke="#888" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

/**
 * Datetimepicker - Componente de seleção de data, hora ou data/hora com suporte a segundos.
 *
 * Permite entrada manual, seleção via calendário ou time picker, e integração mobile com inputs nativos.
 *
 * ## Modos
 * - `"date"`: Apenas data (DD/MM/AAAA)
 * - `"time"`: Apenas hora (HH:mm ou HH:mm:ss)
 * - `"datetime"`: Data e hora (DD/MM/AAAA HH:mm[:ss])
 *
 * ## Acessibilidade
 * - Usa `aria-label` e `aria-errormessage` nos inputs.
 * - Botões e selects do calendário e time picker possuem rótulos acessíveis.
 *
 * ## Mobile
 * - Usa `<input type="date" />` e `<input type="time" step="1" />` para experiência nativa.
 * - No modo `"datetime"`, exibe ambos inputs.
 *
 * ## Máscaras e formatos
 * - Aceita segundos opcionais na digitação e seleção.
 * - Permite customização de máscaras e formatos via props.
 *
 * ## Internacionalização
 * - Mensagens e placeholders customizáveis via `datetimepickerI18nMessages`.
 * - Suporte a `pt` e `en` nativamente.
 *
 * ## Props
 * @typedef {object} DatetimepickerProps
 * @property {Date|string|null} value Valor atual (Date, string ou null)
 * @property {(date: Date|string|null) => void} onChange Callback ao alterar valor
 * @property {"date"|"time"|"datetime"} [mode="datetime"] Modo de operação
 * @property {Date|string} [minDate] Data mínima permitida
 * @property {Date|string} [maxDate] Data máxima permitida
 * @property {"en"|"pt"} [locale="pt"] Idioma
 * @property {DatetimepickerI18nMessages} [datetimepickerI18nMessages] Mensagens customizadas
 * @property {boolean} [emitISO] Se true, retorna string ISO em vez de Date
 * @property {Partial<typeof formats>} [customFormats] Formatos customizados
 * @property {Partial<typeof parseFormats>} [customParseFormats] Formatos de parsing customizados
 * @property {Partial<typeof masks>} [customMasks] Máscaras customizadas
 * @property {string} [placeholder] Placeholder customizado
 * @property {string} [label] Label do campo
 * @property {boolean} [required] Campo obrigatório
 * @property {string} [helperText] Texto de ajuda
 * @property {boolean} [error] Indica erro
 * @property {string} [errorMessage] Mensagem de erro
 * @property {string} [successMessage] Mensagem de sucesso
 * @property {React.ReactNode} [successIcon] Ícone de sucesso
 * @property {string} [className] Classe CSS extra
 * @property {string} [inputClassName] Classe CSS extra para input
 * @property {object} [inputProps] Props extras para input
 * @property {object} [containerProps] Props extras para container
 * @property {React.ReactNode} [tooltip] Tooltip
 * @property {number} [tabIndex] TabIndex
 * @property {boolean} [autoFocus] Autofocus
 * @property {string} [id] ID do campo
 * @property {string} [accessibilityLabel] Label acessível
 * @property {string} [accessibilityErrorMessage] Mensagem de erro acessível
 * @property {(e: React.FocusEvent) => void} [onFocus] Evento de foco
 * @property {(e: React.FocusEvent) => void} [onBlur] Evento de blur
 *
 * @example
 * // DateTime com segundos, modo desktop
 * <Datetimepicker
 *   mode="datetime"
 *   value={value}
 *   onChange={setValue}
 *   locale="pt"
 * />
 *
 * @example
 * // Apenas hora, mobile
 * <Datetimepicker
 *   mode="time"
 *   value={value}
 *   onChange={setValue}
 * />
 *
 * @example
 * // Customizando mensagens e placeholder
 * <Datetimepicker
 *   mode="date"
 *   value={value}
 *   onChange={setValue}
 *   datetimepickerI18nMessages={{ placeholderDate: "Selecione a data" }}
 * />
 *
 * @see DatetimepickerI18nMessages
 */
export default Datetimepicker;